/**
 * Live rates, and the operator override.
 *
 * Each row is one priced pair: what the feed last observed, what an operator has laid over
 * it, and — the column that matters — what a customer's quote actually multiplies by right
 * now. That last value comes from the API rather than being re-derived here, because a
 * board that computed its own answer could show one price while customers traded at
 * another, which is the single failure this page must not have.
 *
 * Two things an operator has to be able to read at a glance and the design leans on:
 *
 * - **An overridden row is marked as overridden.** The badge, the accent on the row, and
 *   the feed price shown alongside are all saying the same thing: this number is ours, not
 *   the market's.
 * - **An override does not expire, and it cannot outlive the feed.** A price you set stands
 *   until someone presses Resolve — nothing puts it back on its own. Every override
 *   therefore shows how long it has been held, so a number set three days ago reads as
 *   three days old rather than as live. Separately, a pair whose observation has aged past
 *   the staleness ceiling prices nothing at all, override or not, and says so.
 *
 * Editing is gated on `rates:write` (finance and superadmin). Everyone else reads the board.
 * Unlike the flags and providers pages, writes here are *not* optimistic: the price is
 * parsed into an exact rational server-side, and showing a number before the API has
 * confirmed which rational it became would be showing a guess about money.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { AdminRateRow } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, Input, LoadingState } from '../components/ui.js';
import { IconAlert, IconRefresh } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime, timeUntil } from '../lib/format.js';
import { formatRate, parseRateInput, rateToInput } from '../lib/rate.js';

const keyOf = (row: AdminRateRow): string => `${row.base}/${row.quote}`;

/**
 * Basis points as a signed percentage, in integer arithmetic.
 *
 * `changeBps / 100` would be the obvious line and would be fine here — this is a display
 * figure, not money. It is done with `Math.trunc` and a remainder anyway, because "we do
 * not divide numbers that came off a rate with `/`" is a habit worth keeping unbroken in a
 * file that also carries prices.
 */
function formatChangeBps(changeBps: number): string {
  const sign = changeBps > 0 ? '+' : changeBps < 0 ? '-' : '';
  const magnitude = Math.abs(changeBps);
  const whole = Math.trunc(magnitude / 100);
  const fraction = String(magnitude % 100).padStart(2, '0');
  return `${sign}${whole}.${fraction}%`;
}

/** What the input starts at: the standing override if there is one, else the feed. */
function baselineOf(row: AdminRateRow): string {
  const source = row.override ?? row.live;
  return source === null ? '' : rateToInput(source);
}

export function RatesPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.listRates(), []);
  /** Per-pair edits in flight. A pair with no entry is showing its stored value. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  function clearDraft(key: string): void {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function replaceRow(updated: AdminRateRow): void {
    state.setData((prev) =>
      prev === null
        ? prev
        : { ...prev, rows: prev.rows.map((r) => (keyOf(r) === keyOf(updated) ? updated : r)) },
    );
  }

  async function save(row: AdminRateRow, draft: string): Promise<void> {
    // Checked here only to spare a round-trip on an obvious slip. The API is the authority
    // and parses the same string into the rational it stores.
    const checked = parseRateInput(draft);
    if (!checked.ok) {
      toast.error('That price cannot be used', checked.message);
      return;
    }

    const key = keyOf(row);
    setBusy(key);
    try {
      const updated = await api.setRateOverride(row.base, row.quote, { price: checked.price });
      replaceRow(updated);
      clearDraft(key);
      toast.success(
        `${row.base}/${row.quote} is now overridden`,
        updated.effective === null
          ? 'It will price at your rate as soon as the feed recovers.'
          : `Quotes now price at ${formatRate(updated.effective)}.`,
      );
    } catch (err) {
      toast.error('Could not set that rate', messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  async function reset(row: AdminRateRow): Promise<void> {
    const key = keyOf(row);
    setBusy(key);
    try {
      const updated = await api.clearRateOverride(row.base, row.quote);
      replaceRow(updated);
      clearDraft(key);
      toast.success(`${row.base}/${row.quote} is back on the feed`, 'The override was removed.');
    } catch (err) {
      toast.error('Could not reset that rate', messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  async function resetAll(): Promise<void> {
    setBusy('*');
    try {
      const board = await api.resetAllRateOverrides();
      state.setData(() => board);
      setDrafts({});
      toast.success('Every rate is back on the feed', 'All operator overrides were removed.');
    } catch (err) {
      toast.error('Could not reset the rates', messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading live rates…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No rate data." />;

  const rows = state.data.rows;
  const overridden = rows.filter((r) => r.override !== null).length;
  const notPricing = rows.filter((r) => r.effective === null).length;
  const ceilingMinutes = Math.round(state.data.maxAgeSeconds / 60);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Live rates</h1>
        <p className="page-head__sub">
          What the market feed says, and what customers are actually quoted. Type a new price to hold a pair above or below the market. It applies immediately, and stays until you press Resolve. It never drifts back on its own.
          {!canWrite && ' Your role can view rates but not change them.'}
        </p>
      </div>

      {notPricing > 0 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          <IconAlert size={18} />
          <div>
            {notPricing === 1 ? 'One pair is' : `${notPricing} pairs are`} not pricing: the feed
            has not been seen for over {ceilingMinutes} minutes. Trades in those pairs are being
            refused rather than booked at an old price. An override cannot bring them back, by
            design.
          </div>
        </div>
      )}

      <Card
        flush
        title="Rate board"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge tone={overridden === 0 ? 'neutral' : 'violet'}>
              {overridden === 0 ? 'All on the feed' : `${overridden} overridden`}
            </Badge>
            {canWrite && overridden > 0 && (
              <Button size="sm" loading={busy === '*'} onClick={() => void resetAll()}>
                <IconRefresh size={14} />
                Resolve all to market
              </Button>
            )}
          </div>
        }
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Pair</th>
                <th className="table__num">Feed price</th>
                <th className="table__num">24h</th>
                <th>Operator price</th>
                <th className="table__num">Quotes use</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <RateRow
                  key={keyOf(row)}
                  row={row}
                  canWrite={canWrite}
                  busy={busy === keyOf(row)}
                  draft={drafts[keyOf(row)] ?? baselineOf(row)}
                  onDraft={(value) => setDrafts((prev) => ({ ...prev, [keyOf(row)]: value }))}
                  onSave={(value) => void save(row, value)}
                  onReset={() => void reset(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function RateRow({
  row,
  canWrite,
  busy,
  draft,
  onDraft,
  onSave,
  onReset,
}: {
  row: AdminRateRow;
  canWrite: boolean;
  busy: boolean;
  draft: string;
  onDraft: (value: string) => void;
  onSave: (value: string) => void;
  onReset: () => void;
}): JSX.Element {
  const stale = row.live === null || row.live.stale;
  const overridden = row.override !== null;
  const baseline = baselineOf(row);
  const dirty = draft !== baseline;
  const changeBps = row.live?.changeBps ?? 0;

  return (
    <tr className={overridden ? 'rate-row--overridden' : undefined}>
      <td>
        <div className="rate-pair">
          <span className="rate-pair__name">
            {row.base}/{row.quote}
          </span>
          {overridden ? (
            <Badge tone="violet" dot>
              Overridden
            </Badge>
          ) : stale ? (
            <Badge tone="red" dot>
              Stale
            </Badge>
          ) : (
            <Badge tone="green" dot>
              Live
            </Badge>
          )}
        </div>
        <div className="rate-pair__meta">
          {row.live === null
            ? 'never observed'
            : `observed ${relativeTime(row.live.observedAt)}`}
          {/* A null `expiresAt` is an override that stands until an operator clears it —
              which is every override written today. Saying "lapses —" there would read as
              a missing value rather than as "this one is not going anywhere". */}
          {overridden && row.override !== null
            ? row.override.expiresAt === null
              ? ` · held ${relativeTime(row.override.setAt)}, stands until cleared`
              : ` · held ${relativeTime(row.override.setAt)}, lapses ${timeUntil(row.override.expiresAt)}`
            : ''}
        </div>
      </td>

      <td className={`table__num${overridden ? ' rate-cell--superseded' : ''}`}>
        {row.live === null ? <span className="faint">no data</span> : formatRate(row.live)}
      </td>

      <td className={`table__num rate-change rate-change--${changeBps < 0 ? 'down' : 'up'}`}>
        {row.live === null ? <span className="faint">no data</span> : formatChangeBps(changeBps)}
      </td>

      <td>
        <div className="rate-input">
          <Input
            inputMode="decimal"
            aria-label={`Operator price for ${row.base}/${row.quote}`}
            value={draft}
            disabled={!canWrite || busy || row.live === null}
            onChange={(e) => onDraft(e.target.value)}
          />
        </div>
      </td>

      <td className="table__num" style={{ fontWeight: 650 }}>
        {row.effective === null ? (
          <span className="faint">not pricing</span>
        ) : (
          formatRate(row.effective)
        )}
      </td>

      <td>
        <div className="rate-actions">
          {canWrite && dirty && (
            <Button size="sm" variant="primary" loading={busy} onClick={() => onSave(draft)}>
              Save
            </Button>
          )}
          {canWrite && overridden && !dirty && (
            <Button
              size="sm"
              disabled={busy}
              onClick={onReset}
              title="Hand this pair back to the market price. Nothing else will."
            >
              Resolve
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
